// resources/js/src/pages/community/ProfilePage.tsx
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CreatePost from "../../components/community/components/post/CreatePost";
import IntroSection from "../../components/community/components/profile/components/IntroSection";
import OverlayGredientColor from "../../components/layouts/OverlayGredientColor";
import PostFeed from "../../components/community/components/post/PostFeed";
import ProfileHeader from "../../components/community/components/profile/components/ProfileHeader";
import ProfileNotAvailable from "../../components/community/components/profile/components/ProfileNotAvailable";
import FriendsGrid from "../../components/community/components/profile/components/FriendsGrid";
import PhotosGrid from "../../components/community/components/profile/components/PhotosGrid";
import AllFriendsModal from "../../components/community/components/profile/components/AllFriendsModal";
import AllPhotosModal from "../../components/community/components/profile/components/AllPhotosModal";
import { useAuthStore } from "../../auth/auth.store";
import { useProfileStore } from "../../store/profileStore";
import { createPostStore } from "../../store/postStore";
import type { FeedMode } from "../../store/postStore";
import { profileApi } from "../../services/profile.api";
import type { ProfileFriend, ProfilePhoto } from "../../services/profile.api";

export function ProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuthStore();
  const {
    profile,
    state,
    isLoading,
    error,
    load,
    canShowPosts,
  } = useProfileStore();

  // ==================== Friends & Photos state ====================
  const [friends, setFriends] = useState<ProfileFriend[]>([]);
  const [photos, setPhotos] = useState<ProfilePhoto[]>([]);
  const [sideLoading, setSideLoading] = useState(false);

  // ==================== Modals ====================
  const [friendsModalOpen, setFriendsModalOpen] = useState(false);
  const [photosModalOpen, setPhotosModalOpen] = useState(false);

  // ==================== Load profile ====================
  useEffect(() => {
    if (!id) return;
    load(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ==================== Load friends + photos ====================
  useEffect(() => {
    if (!id) return;
    if (!profile || !canShowPosts()) {
      setFriends([]);
      setPhotos([]);
      return;
    }

    let cancelled = false;
    setSideLoading(true);

    Promise.allSettled([
      profileApi.getFriends(id),
      profileApi.getPhotos(id),
    ])
      .then(([friendsRes, photosRes]) => {
        if (cancelled) return;
        setFriends(
          friendsRes.status === "fulfilled" ? friendsRes.value.data : []
        );
        setPhotos(
          photosRes.status === "fulfilled" ? photosRes.value.data : []
        );
      })
      .finally(() => {
        if (!cancelled) setSideLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, profile?.id, state]);

  // ==================== mine store ====================
  const useMineStore = useMemo(
    () => createPostStore({ type: "mine" }),
    []
  );

  const feedMode: FeedMode = useMemo(() => {
    if (state === "owner") return { type: "mine" };
    return { type: "user", userId: profile?.id ?? id! };
  }, [state, profile?.id, id]);

  // ==================== Handle action done ====================
  const handleActionDone = async () => {
    if (!profile?.id) return;
    // refresh profile (بدون flicker)
    await load(profile.id);
    // refresh friends/photos
    profileApi.getFriends(profile.id).then((res) => setFriends(res.data));
    profileApi.getPhotos(profile.id).then((res) => setPhotos(res.data));
  };

  // ==================== Loading ====================
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  // ==================== Network error ====================
  if (error && !state) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-3">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => id && load(id)}
          className="px-4 py-2 rounded-lg bg-primary-600 text-white"
        >
          Try Again
        </button>
      </div>
    );
  }

  // ==================== not_found / banned / blocked ====================
  if (
    state === "not_found" ||
    state === "banned" ||
    state === "blocked_by_me" ||
    state === "blocked_by_them"
  ) {
    return <ProfileNotAvailable state={state} />;
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Failed to load profile.
      </div>
    );
  }

  const isOwner = state === "owner";

  // ==================== render ====================
  return (
    <div className="min-h-screen mt-16">
      <OverlayGredientColor />

      <div className="pt-14 mt-5 md:mt-0">
        <ProfileHeader
          name={profile.name}
          friendCount={profile.friendCount}
          coverPhoto={profile.coverUrl}
          profilePicture={profile.avatarUrl}
          state={state}
          userId={profile.id}
          onActionDone={handleActionDone}
        />

        <div className="mx-auto max-w-7xl px-4 py-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* ==================== Left Sidebar ==================== */}
            <div className="space-y-4 lg:col-span-1">
              <IntroSection
                bio={profile.bio ?? ""}
                work=""
                education=""
                location=""
                joinedDate={profile.joinedAt ?? ""}
              />

              {/* Photos */}
              {photos.length > 0 && (
                <PhotosGrid
                  photos={photos}
                  title="Photos"
                  showSeeAll={photos.length >= 6}
                  onSeeAll={() => setPhotosModalOpen(true)}
                />
              )}

              {/* Friends */}
              {friends.length > 0 && (
                <FriendsGrid
                  friends={friends}
                  title="Friends"
                  showSeeAll={friends.length >= 9}
                  onSeeAll={() => setFriendsModalOpen(true)}
                />
              )}
            </div>

            {/* ==================== Main Feed ==================== */}
            <div className="space-y-4 lg:col-span-2">
              {isOwner && <CreatePost useStore={useMineStore} />}

              {canShowPosts() && (
                <PostFeed
                  mode={feedMode}
                  useStoreOverride={isOwner ? useMineStore : undefined}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==================== Modals ==================== */}
      <AllFriendsModal
        isOpen={friendsModalOpen}
        onClose={() => setFriendsModalOpen(false)}
        userId={profile.id}
        initialFriends={friends}
      />

      <AllPhotosModal
        isOpen={photosModalOpen}
        onClose={() => setPhotosModalOpen(false)}
        userId={profile.id}
        initialPhotos={photos}
      />
    </div>
  );
}

export default ProfilePage;